import { AreaConfig, CategoryId } from '#server/models';
import { LabelDraft } from '@/contexts';
import { toSlug, toggleCategoryId, toggleId } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface LabelEditorProps {
  label: LabelDraft;
  areaConfig: AreaConfig;
  onUpdate: (patch: Partial<LabelDraft>) => void;
  onPlaceOnModel: () => void;
  onCaptureCamera: () => void;
}

export const LabelEditor = ({ label, areaConfig, onUpdate, onPlaceOnModel, onCaptureCamera }: LabelEditorProps) => {
  const primaryGroup = areaConfig.categoryGroups.find((g) => g.isPrimary);

  const isPlaced = label.config3D.position.some((v) => v !== 0);
  const isCameraSet = label.config3D.camera.distance !== 100;

  const toggleCategory = (catId: CategoryId) => {
    onUpdate({ categoryIds: toggleCategoryId(label.categoryIds, catId) });
  };

  const toggleUnit = (unitId: number) => {
    onUpdate({ unitIds: toggleId(label.unitIds, unitId) });
  };

  return (
    <div className="border border-[#333] rounded p-4 space-y-3">
      {/* Title / Slug / Description */}
      <div className="space-y-1">
        <Label className="text-[#999]">Title</Label>
        <Input
          value={label.title}
          onChange={(e) => {
            const title = e.target.value;
            onUpdate({ title, slug: toSlug(title) });
          }}
          className="bg-[#2A2A2A] border-[#444] text-[#E8E8E8]"
          placeholder="Label title"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-[#999]">Slug</Label>
        <Input
          value={label.slug}
          onChange={(e) => onUpdate({ slug: e.target.value })}
          className="bg-[#2A2A2A] border-[#444] text-[#E8E8E8]"
          placeholder="label-slug"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-[#999]">Description</Label>
        <Textarea
          value={label.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          className="bg-[#2A2A2A] border-[#444] text-[#E8E8E8]"
          placeholder="Label description"
          rows={2}
        />
      </div>

      {/* 3D Placement — two-step sequence */}
      <div className="rounded border border-[#2A2A2A] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#888] uppercase tracking-wide">3D Placement</p>

        {/* Step 1: Place on Model */}
        <div className="flex items-center gap-2">
          <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold bg-[#333] text-[#888]">
            1
          </span>
          <span className="text-xs text-[#CCC] flex-1">Place on Model</span>
          <span className="text-xs flex-shrink-0" title={isPlaced ? 'Placed' : 'Not yet placed'}>
            {isPlaced ? (
              <span className="text-[#4ADE80]">✓</span>
            ) : (
              <span className="text-[#555]">○</span>
            )}
          </span>
          <button
            onClick={onPlaceOnModel}
            className={`px-2.5 py-1 text-xs rounded border transition-colors flex-shrink-0 ${
              !isPlaced
                ? 'border-[#C3D82E] text-[#C3D82E] hover:bg-[#C3D82E] hover:text-[#1A1A1A]'
                : 'border-[#444] text-[#888] hover:bg-[#333]'
            }`}
          >
            Place
          </button>
        </div>
        {isPlaced && (
          <p className="text-xs text-[#666] font-mono pl-7">
            [{label.config3D.position.map((v) => v.toFixed(3)).join(', ')}]
          </p>
        )}

        {/* Step 2: Capture Camera */}
        <div className="flex items-center gap-2">
          <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold bg-[#333] text-[#888]">
            2
          </span>
          <span className="text-xs text-[#CCC] flex-1">Capture Camera</span>
          <span className="text-xs flex-shrink-0" title={isCameraSet ? 'Camera set' : 'Not yet captured'}>
            {isCameraSet ? (
              <span className="text-[#4ADE80]">✓</span>
            ) : (
              <span className="text-[#555]">○</span>
            )}
          </span>
          <button
            onClick={onCaptureCamera}
            className={`px-2.5 py-1 text-xs rounded border transition-colors flex-shrink-0 ${
              isPlaced && !isCameraSet
                ? 'border-[#C3D82E] text-[#C3D82E] hover:bg-[#C3D82E] hover:text-[#1A1A1A]'
                : 'border-[#444] text-[#888] hover:bg-[#333]'
            }`}
          >
            Capture
          </button>
        </div>
        {isCameraSet && (
          <p className="text-xs text-[#666] font-mono pl-7">
            dist={label.config3D.camera.distance.toFixed(2)}{' '}
            az={label.config3D.camera.rotation.azimuthAngle.toFixed(3)}{' '}
            pol={label.config3D.camera.rotation.polarAngle.toFixed(3)}
          </p>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-1">
        <Label className="text-[#999]">Primary Category</Label>
        <Select
          value={label.primaryCategoryId ? `${label.primaryCategoryId.groupId}:${label.primaryCategoryId.id}` : ''}
          onChange={(e) => {
            if (!e.target.value) {
              onUpdate({ primaryCategoryId: null });
              return;
            }
            const [groupId, id] = e.target.value.split(':').map(Number);
            onUpdate({ primaryCategoryId: { groupId, id } });
          }}
          className="bg-[#2A2A2A] border-[#444] text-[#E8E8E8]"
        >
          <option value="">— none —</option>
          {primaryGroup?.categories.map((cat) => (
            <option key={`${primaryGroup.id}:${cat.id}`} value={`${primaryGroup.id}:${cat.id}`}>
              {cat.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[#999] block">Categories</Label>
        {areaConfig.categoryGroups.map((group) => (
          <div key={group.id} className="space-y-1">
            <p className="text-xs font-semibold text-[#888] uppercase tracking-wide">
              {group.name}{group.isPrimary && <span className="ml-1 normal-case font-normal text-[#555]">(primary)</span>}
            </p>
            <div className="flex flex-wrap gap-2 pl-1">
              {group.categories.map((cat) => {
                const catId: CategoryId = { id: cat.id, groupId: group.id };
                const checked = label.categoryIds.some((c) => c.id === cat.id && c.groupId === group.id);
                return (
                  <label key={`${group.id}:${cat.id}`} className="flex items-center gap-1.5 text-xs text-[#CCC] cursor-pointer">
                    <Checkbox checked={checked} onChange={() => toggleCategory(catId)} />
                    {cat.color && (
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    )}
                    {cat.name}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Units */}
      <div className="space-y-1">
        <Label className="text-[#999] block mb-1">Units</Label>
        <div className="flex flex-wrap gap-2">
          {areaConfig.units.map((unit) => (
            <label key={unit.id} className="flex items-center gap-1.5 text-xs text-[#CCC] cursor-pointer">
              <Checkbox checked={label.unitIds.includes(unit.id)} onChange={() => toggleUnit(unit.id)} />
              <span className="font-mono text-[#C3D82E]">{unit.udc}</span>
              {unit.name}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
